import React from "react";
import { observer, inject } from "mobx-react";
import * as Yup from "yup";

import { Formik, FormikProps } from "formik";
import { Grid, Hidden } from "@material-ui/core";
import { Values } from "Tools/Values";
import { STFormWrapper } from "@src/Components/Ui/STFormWrapper";
import renderCommonFormField from "@src/Components/Common/FormFields/renderCommonFormField";
import { ButtonsForm } from "@src/Components/Common/ButtonsForm/ButtonsForm";
import { IValuesStore } from "@src/Stores/Values/types";
import FormSwitch from "@src/Components/Common/FormFields/FormSwitch/FormSwitch";
import { STFormTitleTypography } from "@src/Components/Ui/STFormTitleTypohraphy";
import FileField from "./FileField/FileField";
import FormMultiselect from "@src/Components/Common/FormFields/FormMultiselect/FormMultiselect";
import { IManagementStore } from "Stores/Management/types";
import { ApplicationLink, ApplicationLinkByLocation } from "@src/Interfaces/Entity/ApplicationLink";
import { Position } from "@src/Interfaces/Entity/Position";
import { Location } from "@src/Interfaces/Entity/Location";
import { STGrid, STApplicationsWrapper, STLocationTextField, STUrlTextField, STApplicationWrapper } from "./styles";
import { STContentWrapper } from "@src/Components/Common/SimpleModal/styles";

const FIVE_MB = 5e6;

const applicationsSchema = Yup.object().shape({
  title: Yup.string().required(() => {
    return Values.getLocalizationValue("fieldRequired");
  }),
  description: Yup.string().required(() => {
    return Values.getLocalizationValue("fieldRequired");
  }),
  commonApplicationUrl: Yup.string()
    .nullable()
    .when("isSameUrlForAllLocations", {
      is: (flag: boolean) => flag,
      then: Yup.string().required(() => {
        return Values.getLocalizationValue("fieldRequired");
      }),
    }),
  fileImage: Yup.mixed().test(
    "maxSize",
    () => Values.getLocalizationValue("maxSize"),
    value => value.size < FIVE_MB
  ),
});

interface IProps {
  title: string;
  initialValues: ApplicationLink;
  onSubmit: (values: ApplicationLink) => void;
  onClose: () => void;
  values?: IValuesStore;
  management?: IManagementStore;
}

@inject("values", "management")
@observer
class ApplicationForm extends React.Component<IProps> {
  handleUrlChange = (event: any, location: Location, formikProps: FormikProps<ApplicationLink>) => {
    const isApplicationExists = formikProps.values.applicationUrls.find(
      application => application.locationId === location.id
    );
    if (!isApplicationExists) {
      formikProps.values.applicationUrls.push({
        applicationUrl: event.target.value,
        locationId: location.id,
      });
    } else {
      formikProps.values.applicationUrls.map(application => {
        if (application.locationId !== location.id) {
          return application;
        }
        application.applicationUrl = event.target.value;
      });
    }
    this.setState({});
  };

  getApplicationUrl = (formikProps: FormikProps<ApplicationLink>, location: Location) => {
    const application = formikProps.values.applicationUrls.find(app => app.locationId === location.id);
    if (application) {
      return application.applicationUrl;
    }
    return "";
  };

  render() {
    return (
      <Formik
        initialValues={this.props.initialValues}
        validationSchema={applicationsSchema}
        onSubmit={this.props.onSubmit}
        render={(formikProps: FormikProps<ApplicationLink>) => (
          <STFormWrapper onSubmit={formikProps.handleSubmit}>
            <STFormTitleTypography>{this.props.title}</STFormTitleTypography>
            <STContentWrapper>
              {renderCommonFormField<ApplicationLink>("title", Values.getLocalizationValue("name"), formikProps)}
              {renderCommonFormField<ApplicationLink>(
                "description",
                Values.getLocalizationValue("description"),
                formikProps
              )}
              <Hidden xsUp={this.props.initialValues.baseApplication}>
                <STGrid container spacing={16} justify="flex-start" alignItems="center" zeroMinWidth>
                  <Grid item>{Values.getLocalizationValue("sameUrlForAllLocations")}</Grid>
                  <Grid item>
                    <FormSwitch<ApplicationLink>
                      trueAs={true}
                      falseAs={false}
                      fieldName="isSameUrlForAllLocations"
                      formikProps={formikProps}
                    />
                  </Grid>
                </STGrid>
                {!formikProps.values.isSameUrlForAllLocations && (
                  <STApplicationsWrapper>
                    {this.props.management.activeLocations.map((location: Location, index: number) => (
                      <STApplicationWrapper key={index}>
                        <STLocationTextField variant="outlined" value={location.name} size="small" disabled />
                        <STUrlTextField
                          variant="outlined"
                          size="small"
                          value={this.getApplicationUrl(formikProps, location)}
                          onChange={(event: any) => this.handleUrlChange(event, location, formikProps)}
                        />
                      </STApplicationWrapper>
                    ))}
                  </STApplicationsWrapper>
                )}
                {formikProps.values.isSameUrlForAllLocations &&
                  renderCommonFormField<ApplicationLink>(
                    "commonApplicationUrl",
                    Values.getLocalizationValue("uRL"),
                    formikProps
                  )}
              </Hidden>
              <FileField<ApplicationLink>
                fieldName="fileImage"
                label={Values.getLocalizationValue("image")}
                formikProps={formikProps}
                imageUrl={this.props.initialValues.imageUrl}
              />
              <Hidden xsUp={this.props.initialValues.baseApplication}>
                <Grid container justify="flex-start" alignItems="center">
                  <Grid item>
                    <FormMultiselect<ApplicationLink, Position>
                      fieldName="positions"
                      label={Values.getLocalizationValue("positions")}
                      formikProps={formikProps}
                      items={this.props.management.activePositions}
                      keyAsValue="id"
                      keyAsTitle="name"
                      allowEmpty
                    />
                  </Grid>
                </Grid>
              </Hidden>

              <STGrid container justify="flex-start" alignItems="center">
                <Grid item>{Values.getLocalizationValue("mobile")}</Grid>
                <Grid item>
                  <FormSwitch<ApplicationLink>
                    trueAs={true}
                    falseAs={false}
                    fieldName="mobile"
                    formikProps={formikProps}
                  />
                </Grid>
              </STGrid>
              <Grid container justify="flex-start" alignItems="center">
                <Grid item>{Values.getLocalizationValue("desktop")}</Grid>
                <Grid item>
                  <FormSwitch<ApplicationLink>
                    trueAs={true}
                    falseAs={false}
                    fieldName="desktop"
                    formikProps={formikProps}
                  />
                </Grid>
              </Grid>
              <Hidden xsUp={this.props.initialValues.baseApplication}>
                <Grid container justify="flex-start" alignItems="center">
                  <Grid item>{Values.getLocalizationValue("active")}</Grid>
                  <Grid item>
                    <FormSwitch<ApplicationLink>
                      trueAs={this.props.values.states.Active}
                      falseAs={this.props.values.states.Deactivated}
                      fieldName="state"
                      formikProps={formikProps}
                    />
                  </Grid>
                </Grid>
              </Hidden>
            </STContentWrapper>
            <ButtonsForm onSubmit={formikProps.handleSubmit} onClose={this.props.onClose} />
          </STFormWrapper>
        )}
      />
    );
  }
}

export default ApplicationForm;
